import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, ToggleButtonGroup, ToggleButton, Tooltip,
  TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { OwnershipFilter, PatientBoardConfig, PatientCardVM, phaseIndex } from './types';
import PatientBoardTable, { SortDirection } from './PatientBoardTable';
import PatientBoardSplitView from './PatientBoardSplitView';
import PatientBoardKanban from './PatientBoardKanban';

type ViewMode = 'split' | 'table' | 'board';

const URGENCY_INDEX: Record<string, number> = { emergent: 0, urgent: 1, routine: 2 };

const sortValue = (p: PatientCardVM, field: string, config: PatientBoardConfig): number | string => {
  switch (field) {
    case 'name':              return p.name.toLowerCase();
    case 'disposition':       return p.scoreCell.score;
    case 'location':          return p.locationSecondary.toLowerCase();
    case 'physician':         return p.physician.toLowerCase();
    case 'diagnosis':         return p.diagnosis.toLowerCase();
    case 'expectedDischarge': return p.expectedDischargeMs;
    case 'currentLOS':        return p.currentLOS;
    case 'phase':             return phaseIndex(config, p.phaseKey);
    case 'urgency':           return URGENCY_INDEX[p.urgency?.label.toLowerCase() ?? 'routine'] ?? 2;
    case 'assigned':          return (p.assignedToName ?? '￿').toLowerCase();
    default:                  return 0;
  }
};

interface Props {
  patients: PatientCardVM[];
  config: PatientBoardConfig;
  ownershipFilter?: OwnershipFilter;
}

const PatientBoardList: React.FC<Props> = ({ patients: allPatients, config, ownershipFilter = 'ALL' }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortBy, setSortBy] = useState<string>('expectedDischarge');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSort = (field: string) => {
    if (field === sortBy) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const patients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const searched = q
      ? allPatients.filter(p => config.searchText(p).toLowerCase().includes(q))
      : allPatients;

    return [...searched].sort((a, b) => {
      const av = sortValue(a, sortBy, config);
      const bv = sortValue(b, sortBy, config);
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allPatients, config, searchQuery, sortBy, sortDirection]);

  const title = config.title(ownershipFilter);

  return (
    <Box sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 }, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography variant="h4" fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{patients.length} patients</Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TextField
            size="small"
            placeholder="Quick filter"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ width: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <Tooltip title="Split view">
              <ToggleButton value="split" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <VerticalSplitIcon sx={{ fontSize: '1rem' }} /> Split
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Table view">
              <ToggleButton value="table" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <TableRowsIcon sx={{ fontSize: '1rem' }} /> Table
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Board view">
              <ToggleButton value="board" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <ViewColumnIcon sx={{ fontSize: '1rem' }} /> Board
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      {viewMode === 'split' && <PatientBoardSplitView patients={patients} config={config} />}
      {viewMode === 'table' && (
        <PatientBoardTable
          patients={patients}
          config={config}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}
      {viewMode === 'board' && <PatientBoardKanban patients={patients} config={config} />}
    </Box>
  );
};

export default PatientBoardList;
